import { useEffect, useState } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Stack,
  Group,
  Checkbox,
  Text,
  Divider,
  Accordion,
  Badge,
  Loader,
  Center,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  useAllPermissions,
  useCreateRole,
  useUpdateRole,
} from '@/models/adminUsers';
import type { Role, Permission } from '@/types/admin';

interface RoleFormModalProps {
  opened: boolean;
  onClose: () => void;
  role: Role | null;
  isEditMode: boolean;
}

export function RoleFormModal({
  opened,
  onClose,
  role,
  isEditMode,
}: RoleFormModalProps) {
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<
    Set<number>
  >(new Set());

  const form = useForm({
    initialValues: {
      name: '',
      description: '',
    },
    validate: {
      name: (value) =>
        value.trim().length > 0 ? null : 'Role name is required',
    },
  });

  // Hooks from model
  const { data: allPermissionsData, isLoading: permissionsLoading } =
    useAllPermissions();
  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole();

  // Initialize form and selected permissions when editing
  useEffect(() => {
    if (role && isEditMode && opened && allPermissionsData) {
      form.setValues({
        name: role.name || '',
        description: role.description || '',
      });

      // Map permission names to IDs
      const permissionIds = new Set<number>();
      role.permissions?.forEach((permName) => {
        const perm = allPermissionsData.permissions.find(
          (p: Permission) => p.name === permName,
        );
        if (perm) {
          permissionIds.add(perm.id);
        }
      });
      setSelectedPermissionIds(permissionIds);
    } else if (!isEditMode && opened) {
      form.reset();
      setSelectedPermissionIds(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, isEditMode, opened, allPermissionsData]);

  const handleTogglePermission = (permissionId: number) => {
    const newSelected = new Set(selectedPermissionIds);
    if (newSelected.has(permissionId)) {
      newSelected.delete(permissionId);
    } else {
      newSelected.add(permissionId);
    }
    setSelectedPermissionIds(newSelected);
  };

  const handleSelectAll = (resource: string) => {
    const resourcePerms =
      allPermissionsData?.permissions.filter(
        (p: Permission) => p.resource === resource,
      ) || [];
    const newSelected = new Set(selectedPermissionIds);
    resourcePerms.forEach((p: Permission) => newSelected.add(p.id));
    setSelectedPermissionIds(newSelected);
  };

  const handleDeselectAll = (resource: string) => {
    const resourcePerms =
      allPermissionsData?.permissions.filter(
        (p: Permission) => p.resource === resource,
      ) || [];
    const newSelected = new Set(selectedPermissionIds);
    resourcePerms.forEach((p: Permission) => newSelected.delete(p.id));
    setSelectedPermissionIds(newSelected);
  };

  const handleSubmit = form.onSubmit((values) => {
    const permissionIds = Array.from(selectedPermissionIds);

    if (isEditMode && role) {
      updateMutation.mutate(
        {
          roleId: role.id,
          name: values.name,
          description: values.description,
          permissionIds,
        },
        {
          onSuccess: () => {
            handleClose();
          },
        },
      );
    } else {
      createMutation.mutate(
        {
          name: values.name,
          description: values.description,
          permissionIds,
        },
        {
          onSuccess: () => {
            handleClose();
          },
        },
      );
    }
  });

  const handleClose = () => {
    form.reset();
    setSelectedPermissionIds(new Set());
    onClose();
  };

  // Group permissions by resource
  const groupedPermissions: Record<string, Permission[]> = {};
  if (allPermissionsData?.permissions) {
    allPermissionsData.permissions.forEach((perm: Permission) => {
      if (!groupedPermissions[perm.resource]) {
        groupedPermissions[perm.resource] = [];
      }
      groupedPermissions[perm.resource].push(perm);
    });
  }

  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditMode ? 'Edit Role' : 'Create Role'}
      size="lg"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {/* Role Name and Description */}
          <TextInput
            label="Role Name"
            placeholder="e.g., Sales Manager"
            required
            {...form.getInputProps('name')}
          />

          <Textarea
            label="Description"
            placeholder="Brief description of this role"
            minRows={2}
            {...form.getInputProps('description')}
          />

          <Divider />

          {/* Permissions Section */}
          <div>
            <Group justify="space-between" mb="sm">
              <Text size="sm" fw={600}>
                Permissions
              </Text>
              <Badge variant="light" color="blue">
                {selectedPermissionIds.size} selected
              </Badge>
            </Group>

            {permissionsLoading ? (
              <Center p="xl">
                <Loader size="sm" />
              </Center>
            ) : (
              <Accordion variant="contained">
                {Object.entries(groupedPermissions).map(([resource, perms]) => {
                  const allSelected = perms.every((p) =>
                    selectedPermissionIds.has(p.id),
                  );
                  const someSelected = perms.some((p) =>
                    selectedPermissionIds.has(p.id),
                  );
                  const selectedCount = perms.filter((p) =>
                    selectedPermissionIds.has(p.id),
                  ).length;

                  return (
                    <Accordion.Item key={resource} value={resource}>
                      <Accordion.Control>
                        <Group justify="space-between">
                          <Group gap="xs">
                            <Text size="sm" fw={500} tt="capitalize">
                              {resource}
                            </Text>
                            {someSelected && (
                              <Badge size="sm" variant="light" color="blue">
                                {selectedCount}/{perms.length}
                              </Badge>
                            )}
                          </Group>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="sm">
                          <Group gap="xs">
                            <Button
                              size="xs"
                              variant="light"
                              onClick={() => handleSelectAll(resource)}
                              disabled={allSelected}
                            >
                              Select All
                            </Button>
                            <Button
                              size="xs"
                              variant="subtle"
                              onClick={() => handleDeselectAll(resource)}
                              disabled={!someSelected}
                            >
                              Deselect All
                            </Button>
                          </Group>
                          <Stack gap="xs">
                            {perms.map((perm) => (
                              <Checkbox
                                key={perm.id}
                                checked={selectedPermissionIds.has(perm.id)}
                                onChange={() => handleTogglePermission(perm.id)}
                                label={
                                  <div>
                                    <Text size="sm" fw={500}>
                                      {perm.action}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {perm.description}
                                    </Text>
                                  </div>
                                }
                              />
                            ))}
                          </Stack>
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            )}
          </div>

          {/* Actions */}
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={handleClose}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button type="submit" loading={isMutating}>
              {isEditMode ? 'Save Changes' : 'Create Role'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
